/**
 * Google Identity Services (GIS) loader for the composer.
 *
 * One script, loaded once, then `renderGoogleButton` draws the official button
 * into a container and hands the credential (a Google id_token) to the caller.
 * The composer never inspects the token — it goes straight to the backend,
 * which verifies it against Google's JWKS with an explicit audience allow-list.
 */
import Config from '../Config.jsx';

let gsiPromise = null;

export function loadGsi(locale) {
  if (gsiPromise) {
    return gsiPromise;
  }
  gsiPromise = new Promise((resolve, reject) => {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      resolve();
      return;
    }
    const existing = document.getElementById('gsi-client-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', (e) => reject(e));
      return;
    }
    const script = document.createElement('script');
    script.id = 'gsi-client-script';
    // `hl` pins the BUTTON language. The GsiButtonConfiguration.locale option
    // is not honoured (verified: the button rendered in the browser locale —
    // Japanese — with it set), so the script URL is the working knob. Locale is
    // therefore fixed at first load; a language switch re-localises the button
    // on the next full page load, which the composer's switcher already does.
    script.src = 'https://accounts.google.com/gsi/client'
      + (locale ? '?hl=' + encodeURIComponent(locale) : '');
    script.async = true;
    script.onload = () => resolve();
    script.onerror = (e) => {
      gsiPromise = null; // allow a retry after a network failure
      reject(e);
    };
    document.body.appendChild(script);
  });
  return gsiPromise;
}

/**
 * Render the Google button into `container`; `onCredential(idToken)` fires on
 * a successful sign-in. Safe to call again on the same container (GIS
 * replaces the button). Returns a promise that rejects if GIS cannot load —
 * callers hide the section rather than showing a dead button.
 */
export async function renderGoogleButton(container, onCredential, options = {}) {
  if (!container) {
    return;
  }
  await loadGsi(options.locale);
  window.google.accounts.id.initialize({
    client_id: Config.GOOGLE_WEB_CLIENT_ID(),
    callback: (response) => {
      const credential = response && response.credential;
      if (credential) {
        onCredential(credential);
      }
    },
  });
  window.google.accounts.id.renderButton(
    container,
    Object.assign({ theme: 'outline', size: 'large', width: 280 }, options),
  );
}
